import { InfiniteData, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Instruction, InstructionType as GenericInstructionType, IpRight, IpRightStatus, Renewal, RenewalState } from "../interfaces/renewal_types";
import { parseBackend } from "src/backend";
import _ from "lodash";
import { createContext, useContext } from "react";
import { ErrorMessage } from "src/Messages";
import { Member } from "src/patents/patents";
import { useFilteredPatents } from "src/filter/FilteredPatents";
import { usePatents } from "src/patents/PatentsProvider";
import { diffDays } from "src/utils/dates";

export type PagedResponse<T> = {
  items: T[];
  num_pages: number;
}

export const entity_sizes = ["large", "small", "micro", "local", "nonprofit", "special", "undiscounted", "individual"] as const;
export const owner_types = ["Individual", "Non-profit Organization", "Private Company", "Public Company", "Public Research Institute", "Other"] as const;

export interface PROwner {
  id?: string;  // UUID
  name: string;
  country: string;
  entity_size: typeof entity_sizes[number] | null;
  type: typeof owner_types[number] | null;
  industry: string | null;
  company_website: string | null;
}

export interface PRCostCenter {
  id?: string // UUID
  name: string
  invoice_currency: string
  owner_id: string
  country: string
  vat_number: string | null
  address_line_1: string | null
  address_line_2: string | null
  city: string | null
  postal_code: string | null
  state: string | null
  // skip banking stuff
  bank_name: string | null
  bank_address_line: string | null
  swift_code: string | null
  alternative_transfer_code: string | null
  iban_code: string | null
  account_number: string | null
  rnot_emails: (string | null)[] // should be four emails
  billing_emails: (string | null)[] // should be four emails
}

export const IpMetaContext = createContext({
  owners: [] as PROwner[],
  postOwner: (owner: PROwner) => Promise.resolve({}),
  //deleteOwner: ({id: string}) => Promise.resolve({}),

  costCenters: [] as PRCostCenter[],
  ownerByCostCenterId: {} as Record<string, string>,
  costCenterById: {} as Record<string, PRCostCenter>,
  //postCostCenter: (costCenter: PRCostCenter) => Promise.resolve({}),
  //deleteCostCenter: ({id: string}) => Promise.resolve({}),
})

export function useIpContext() {
  return useContext(IpMetaContext);
}


export type PrIpType = "patent" |  "utility_model" | "design" | "trademark";

export function toPrIpType(ipType: string): PrIpType {
  switch(ipType) {
    case "patent":
    case "utility_model":
    case "design":
    case "trademark":
      return ipType.replace(' ', '_') as PrIpType;
    default:
      return "patent";
  }
}

export type PRIpRight = {
  id?: string;
  cost_center_id: string;

  ip_partner_patent_ref: string; // use family member ID 
  ip_owner_patent_ref?: string;
  ip_owner_family_ref?: string;
  internal_patent_ref?: string;

  type: PrIpType;
  country: string;
  region: "EA" | "EP" | "AP" | "EM" | "WO" | null;

  annuity_year?: number;
  application_number?: string;
  publication_number?: string;
  grant_number?: string;
  priority_number?: string;

  family_id?: string;
  local_title: string | null;

  current_renewal_id?: string;
  // updated: string;
  created_at: string;
  expiration_date: string;
  is_in_surcharge: boolean;
  is_granted: boolean;
  is_manual: boolean;
  is_finished: boolean;
  is_dead: boolean;
  is_later: boolean;

  instruction?: string;
  instruction_reason?: InstructionReason,
  last_renewed?: string;

  national_database_url?: string;
  // epo_family_id?: string;
  is_pending_instruction_change_review: boolean;
  should_be_renewed_in_surcharge: boolean;

  claim_count?: number
}

export function toIpRightStatus(prIpRight: PRIpRight): IpRightStatus {
  if (prIpRight.is_manual)
    return "Processing"
  else if (prIpRight.instruction === 'cancel')
    return "Stopped"
  else if (prIpRight.current_renewal_id)
    return "Ready"
  else if (prIpRight.is_finished)
    return "Paid-for-life"
  else if (prIpRight.is_dead)
    return "Stopped"
  else
    return "Expired"
}


export function toIpRight(prIpRight: PRIpRight, ownerByCostCenterId: Record<string, string>): IpRight {
  return {
    id: prIpRight.id,
    // TODO: check with ID or REF to tak
    clientReference: prIpRight.ip_owner_patent_ref,
    clientUniqueId: prIpRight.internal_patent_ref,
    publicationNumber: prIpRight.publication_number,
    grantNumber: prIpRight.grant_number,
    startDate: prIpRight.created_at,
    status: toIpRightStatus(prIpRight),
    renewalIds: prIpRight.current_renewal_id ? [prIpRight.current_renewal_id] : [],
    renewals: [], // TODO: This should be populated with actual renewal data
    costCenters: { [prIpRight.cost_center_id || 'default']: 100 }, 
    owner: ownerByCostCenterId[prIpRight.cost_center_id] || 0,
  };
}

export function useIpOwners() {
  const queryClient = useQueryClient()

  const owners_url = '/api/patent-renewal/pass-through/api/v1/owners'

  const queryKey = ['pr', 'owners']
  const { data } = useQuery<PagedResponse<PROwner>>({
    queryKey: queryKey,
    queryFn: async () => parseBackend(fetch(owners_url)),
  })
  const owners = data?.items ?? []
  //console.log({owners})

  function reload() {
    queryClient.invalidateQueries({
      queryKey,
      //queryFn,    // TODO CHECK MIGRATION
      refetchType: 'all',
    })
  }

  const mutationConfig = {
    onError: (err: ErrorMessage, payload: PROwner, context) => {
      console.error(err.message)
      queryClient.setQueryData(queryKey, context.previousEntities)
    },
    onSettled: reload,
    enabled: true, // TODO
  }

  const headers = {
    'Content-Type': 'application/json',
    'accept': 'application/json',
  }

  const postMutation = useMutation<PROwner, ErrorMessage, PROwner>({
    mutationFn: (payload: PROwner) =>
      payload.id
        ? parseBackend(fetch(`${owners_url}/${payload.id}`, { method: 'PATCH', body: JSON.stringify(payload), headers }))
        : parseBackend(fetch(owners_url, { method: 'POST', body: JSON.stringify(payload), headers })),
    ...mutationConfig,
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey })
      const previousEntities = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, old => {
        // check if old is of type array
        if (Array.isArray(old)) {
          //console.log({old})
          const p_id = payload.id
          return p_id ? old.map(o => (o.id === p_id ? payload : o)) : [...old, payload]
        } else {
          return [payload]
        }
      })
      return { previousEntities }
    },
  })

  const ownerById = _.keyBy(owners, o => o.id)
  return { owners, postOwner: postMutation.mutateAsync, ownerById }
}

export function useIpCostCentersPerOwner(owner_id: string) {
  const queryClient = useQueryClient()
  const queryKey = ['pr', 'costCenters', owner_id]

  const { data } = useQuery<PagedResponse<PRCostCenter>>({
    queryKey: queryKey,
    queryFn: async () => parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/owners/${owner_id}/cost_centers`)),
  })
  const costCenters = data?.items ?? []

  function reload() {
    queryClient.invalidateQueries({
      queryKey: queryKey.slice(0, 2),
      //queryFn,    // TODO CHECK MIGRATION
      refetchType: 'all',
    })
  }

  const headers = {
    'Content-Type': 'application/json',
    'accept': 'application/json',
  }

  const mutationConfig = {
    onError: (err: ErrorMessage, payload: PRCostCenter, context) => {
      console.error(err.message)
      queryClient.setQueryData(queryKey, context.previousEntities)
    },
    onSettled: reload,
    enabled: true, // TODO
  }

  const postMutation = useMutation<PRCostCenter, ErrorMessage, PRCostCenter>({
    mutationFn: (payload: PRCostCenter) =>
      payload.id
        ? parseBackend(
            fetch(`/api/patent-renewal/pass-through/api/v1/cost_centers/${payload.id}`, { method: 'PATCH', body: JSON.stringify(payload), headers }),
          )
        : parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/owners/${payload.owner_id}/cost_centers`, { method: 'POST', body: JSON.stringify(payload), headers })),
    ...mutationConfig,
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey })
      const previousEntities = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, old => {
        // check if old is of type array
        if (Array.isArray(old)) {
          //console.log({old})
          const p_id = payload.id
          return p_id ? old.map(o => (o.id === p_id ? payload : o)) : [...old, payload]
        } else {
          return [payload]
        }
      })
      return { previousEntities }
    },
  })

  async function deleteCostCenter(payload: PRCostCenter) {
    const resp = await parseBackend(
      fetch(`/api/patent-renewal/pass-through/api/v1/cost_centers/${payload.id}`, { method: 'DELETE' }),
    )
    //console.log({resp})
    reload()
    return resp
  }

  return { costCenters, postCostCenter: postMutation.mutateAsync, deleteCostCenter }
}


export function useIpRights() {
  const {memberByReference} = useFilteredPatents()

  const {owners} = useIpContext()
  //console.log({owners})

  const data = useQueries({
    queries: owners.map(owner => ({
      queryKey: ['pr', 'ip', owner.id],
      queryFn: async () => {
        const result: PagedResponse<PRIpRight> = await parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/owners/${owner.id}/ips?limit=1000`))
        //console.log({result})
        return result.items.filter((i: PRIpRight) => i.ip_owner_patent_ref in memberByReference)
      }
    }))
  })
  //console.log({data})

  const ipRights = data.flatMap(d => d.data ?? [])
  const isLoading = data.some(d => d.isLoading)
  //console.log({ipRights})

  const ipRightById = _.keyBy(ipRights, ip => ip.id)
  const ipRightByRef = _.keyBy(ipRights, (ip: PRIpRight) => ip.ip_owner_patent_ref)

  return { owners, ipRights, ipRightById, ipRightByRef, isLoading }
}

type RenewalsPageParams = {ownerIdx: number, offset: number}

export function useRenewals({start, end, states}: {start?: string; end?: string; states?: RenewalState[]}) {
  const { owners } = useIpOwners();

  // status as array of strings?
  const prStates = states?.flatMap(state => {
    switch(state) {
      case "Open":
        return ["initial_state", "error"];
        case "Instructed":
        return ["pending_payment", "pending_patent_office"];
      case "Paid":
        return ["renewed", "renewed_by_3rd_party"];
      case "Cancelled":
        return ["not_renewed"];
        default:
        return [];
    }
  }) ?? [];

  const limit = 1000
  const query = {
    limit: '' + limit, // lazy pagination
    states: prStates.length > 0 ? prStates.join(',') : undefined, // TODO check is array and how it works
  }

  const queryParams = new URLSearchParams(query as Record<string, string>).toString()

  const getPage = async (p: {pageParam: RenewalsPageParams}) => {
    const {ownerIdx, offset} = p.pageParam
    // console.log(`Going for Owner ${ownerIdx} with offset ${offset}`)
    const url = `/api/patent-renewal/pass-through/api/v1/owners/${owners[ownerIdx].id}/renewals?${queryParams}&offset=${offset}`
    return parseBackend(fetch(url))
  }

  const {
    data: pages, error, hasNextPage, isFetching, fetchNextPage, isLoading,
  } = useInfiniteQuery<PagedResponse<PRRenewal>, ErrorMessage, InfiniteData<PagedResponse<PRRenewal>>, string[], RenewalsPageParams>({
    queryKey: ['pr', 'renewals'],
    enabled: owners?.length > 0,
    initialPageParam: {ownerIdx: 0, offset: 0},
    // @ts-ignore TODO MIGRATION CHECK
    queryFn: getPage,
    getNextPageParam: (lastPage, pages, lastPageParam) => {
      const ownerIdx = lastPageParam.ownerIdx;
      const lastOffset = lastPageParam.offset;
      const maxOffset = (lastPage.num_pages - 1) * limit;
      //console.log({lastOffset, maxOffset})
      if (lastOffset >= maxOffset) {
        // go to next owner or undefined
        const newOwnerIdx = ownerIdx + 1;
        if (newOwnerIdx >= owners.length) {
          // we are at the end
          return undefined // Stops
        } else {
          return {offset: 0, ownerIdx: newOwnerIdx}
        }
      } else {
        return {ownerIdx, offset: lastOffset + limit}
      }
    }
  })
  if (hasNextPage) {
    fetchNextPage()
  }

  const _renewals = (pages ? pages.pages.flatMap(p => p.items ?? []) : [])
    .filter(isEmptyRenewal)

  let renewals: PRRenewal[]
  if (start !== undefined || end !== undefined) {
    const check = 
      start && end 
      ? (d: string) => start <= d && d <= end
      : start
      ? (d: string) => start <= d
      : (d: string) => d <= end
    renewals = _renewals.filter(r => check(r.payment_date))
  } else {
    renewals = _renewals
  }

  //console.log({renewals})

  return { renewals: renewals ?? [], isLoading }
}

export function useRenewal(id: string) {
  const {memberByReference} = usePatents()
  const { ownerByCostCenterId } = useIpContext()

  const {data: prRenewal} = useQuery<PRRenewal>({
    queryKey: ['pr', 'renewals', id],
    queryFn: () => {
      const url = `/api/patent-renewal/pass-through/api/v1/renewals/${id}`
      return parseBackend(fetch(url))
    },
    enabled: !!id,
  })
  const ip_id = prRenewal?.ip_id
  const {data: prIpRight} = useQuery<PRIpRight>({
    queryKey: ['pr', 'ip', ip_id],
    queryFn: () => {
      const url = `/api/patent-renewal/pass-through/api/v1/ips/${ip_id}`
      return parseBackend(fetch(url))
    },
    enabled: !!ip_id
  })

  const {instruct} = useInstruction({ip_id})
  if (!prIpRight || !prRenewal) {
    return {renewal: undefined, instruct, ipRight: undefined, member: undefined}
  }

  const member = memberByReference[prIpRight?.ip_owner_patent_ref]

  const ipRight = toIpRight(prIpRight, ownerByCostCenterId)
  const renewal = toRenewal(prRenewal, prIpRight)

  return {renewal, instruct, ipRight, member}
}


export type PRRenewal = {
  id: string;
  ip_id: string;

  state: PRRenewalState; // This should be an enum or a specific type
  fees?: PRFee; // This should be more specific based on your fee structure

  date_of_payment_sent?: string; // date-time
  date_renewed?: string; // date-time

  expected_rnot_sent_date?: string // date-time
  expected_invoice_sent_date?: string // date-time; instruction cutoff is 10 days before

  payment_date?: string; // date-time
  renewal_date?: string; // date-time
  surcharge_date?: string; // date-time

  annuity_year?: string;
}

export type PRFee = {
  official_fee: FeeAmount;
  surcharge_fee: FeeAmount;
  agent_fee: FeeAmount;
  local_fee: FeeAmount;
  service_fee: FeeAmount;
  late_service_fee: FeeAmount;
  transaction_fee: FeeAmount;
  transaction_service_fee: FeeAmount;
  fx_deposit: FeeAmount;
}

export type FeeAmount = {
  amount: number | string;
  currency: string;
}

export function useInstruction(args: {iprByRenewalId?: Record<string, string>, ip_id?: string}) {
  const client = useQueryClient()

  const _instruct = async (renewalId: string, instruction: Instruction | undefined) =>  {
    const ip_id = 'ip_id' in args ? args.ip_id : args.iprByRenewalId?.[renewalId]
    if (ip_id === undefined) {
      console.warn(`Did not find IP Right ID for Renewal ID ${renewalId}!`)
      return ;
    }
    const reason = 'RBC' // Requested by Customer
    const body = {
      instruction: toPrInstructionType(instruction.instruction),
      reason,
      description: '' + (instruction.commentId ?? ''),
      documentation_url: null,
    }
    await fetch(`/api/patent-renewal/pass-through/api/v1/ips/${ip_id}/instructions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
  }

  const instruct = async (renewalId: string, instruction: Instruction | undefined) => {
    await _instruct(renewalId, instruction)
    client.invalidateQueries({queryKey: ["pr", "ip"]})
    client.invalidateQueries({queryKey: ["pr", "renewals"]})
    return Promise.resolve() // TODO: we should return something meaningful?
  }

  const  bulkInstruct = async (instructions: Record<string, Instruction>) => {
    await Promise.all(Object.entries(instructions).map(([renewalId, instruction]) => _instruct(renewalId, instruction)))
    client.invalidateQueries({queryKey: ["pr", "ip"]})
    client.invalidateQueries({queryKey: ["pr", "renewals"]})
    return Promise.resolve() // TODO: we should return something meaningful?
  }

  return {instruct, bulkInstruct}
}

type InstructionReason = 
"ATC" | // - Abandoning this case
"MBL" | // - Managed by licensee
"HOP" | // - Handled by other provider
"SMR" | // - Self-managed renewal
"OT"  | //- Ownership transferred
"CMP" | // - Co-managed by partner
"RBC" | // - requested by customer
"O"     // - Other reason

type InstructionType = "renew" | "cancel" | "client_is_responsible" | "not_set"

function toPrInstructionType(instructionType: GenericInstructionType): InstructionType {
  switch(instructionType) {
    case 'Cancel': return 'cancel';
    case 'Pay': return 'renew';
  }
}

export type PRInstruction = {
  instruction: InstructionType
  reason: InstructionReason
  description: string | null
  documentation_url: string | null
  created_at: string
}
type PRRenewalState = "initial_state" | "pending_payment" | "pending_patent_office" | "renewed" | "renewed_by_3rd_party" | "not_renewed" | "error"

// fees: {official_fee: {amount: "350.000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000", currency: "EUR"}, surcharge_fee: {amount: "0E-90", currency: "EUR"}, agent_fee: {amount: "0E-90", currency: "EUR"}, local_fee: {amount: "0.00", currency: "EUR"}, service_fee: {amount: "80.000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000", currency: "EUR"}, …}

const format = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})

export function aggregateFee(fees: PRFee) {
    const amount = _(fees)
      .toPairs()
      .map(([_, amount]: [any, FeeAmount]) => 
        typeof amount.amount === 'string' ? parseFloat(amount.amount) : amount.amount)
      .sum()
    //console.log({fees: prRenewal.fees, amount})
    const currency = fees.official_fee.currency
    return {amount: format.format(amount), currency}
}

function isEmptyRenewal(prRenewal: PRRenewal): boolean {
  return prRenewal.renewal_date !== undefined
}

export function toRenewal(prRenewal: PRRenewal, ipRight: PRIpRight, instruction?: PRInstruction): Renewal {
  const fee = {}
  if (typeof prRenewal.fees === 'object') {
    const {amount, currency} = aggregateFee(prRenewal.fees)
    fee[currency] = amount;
  }
  let instructionDueDate = undefined
  if (prRenewal.expected_invoice_sent_date) {
    instructionDueDate = new Date(prRenewal.expected_invoice_sent_date)
    instructionDueDate.setDate(instructionDueDate.getDate() - 10)
    instructionDueDate = instructionDueDate.toISOString().slice(0, 10)
  }
  return {
    id: prRenewal.id,
    ipRightId: prRenewal.ip_id,
    state: toRenewalState(prRenewal.state),
    updated: instruction?.created_at,
    created: null,
    feeDueDate: prRenewal.payment_date,
    instructionDueDate,
    instruction: toInstruction(ipRight, instruction),
    fee,
    isAutoPay: true, // PR is always on auto-pay
    annuity: prRenewal.annuity_year,
  };
}

export function toInstruction(ipRight: PRIpRight | undefined, instruction?: PRInstruction): Instruction | undefined {
  // ignore any instruction which is not Requested By Customer
  if (!ipRight || ipRight.instruction_reason !== 'RBC') return undefined
  return {
    instruction: ipRight?.instruction === "renew" ? "Pay" : "Cancel",
    created: instruction?.created_at ?? ipRight.last_renewed ?? ipRight.created_at,
  }
}

function toRenewalState(prRenewalState: PRRenewalState): RenewalState {
  switch(prRenewalState) {
    case "renewed":
    case "renewed_by_3rd_party":
      return "Paid"
    case "not_renewed":
      return "Cancelled"
    case "pending_payment":
    case "pending_patent_office":
      return "Instructed"
    case "initial_state":
    case "error":
    default:
      return "Open"
  }
}

// [renewal.id, PRInstruction[]]; latest first
function splitInstructions(renewals: PRRenewal[], instructions?: PRInstruction[]): Record<string, PRInstruction[]> {
  const result = {}
  if (!instructions || !Array.isArray(instructions)) return result

  instructions.forEach(instruction => {
    let renewal = undefined
    let delta_t = undefined
    renewals.forEach(r => {
      const dt = diffDays(instruction.created_at, r.renewal_date)
      if (dt > 0 && (delta_t === undefined || dt < delta_t)) {
        delta_t = dt;
        renewal = r;
      }
    })
    //console.log({renewal, delta_t})
    if (renewal) {
      result[renewal.id] = _.sortBy(
        [...(result[renewal.id] ?? []), instruction],
        (i: PRInstruction) => -i.created_at
      )
    }
  })
  return result
}

export function toRenewals(prIpRight: PRIpRight, prRenewals: PRRenewal[], instructions: PRInstruction[]) {
  const instructionsPerRenewalId = splitInstructions(prRenewals ?? [], instructions ?? [])
  const renewals = prRenewals?.map(r => {
    const specificInstructions = instructionsPerRenewalId[r.id]
    //console.log({specificInstructions})
    const latestInstruction = specificInstructions ? _.maxBy(specificInstructions, i => i.created_at) : null
    return toRenewal(r, prIpRight, latestInstruction)
  }) ?? []
  return renewals
}


export function useIpRight(member: Member) {
  const { ownerByCostCenterId } = useIpContext()

  const {data, isLoading: isLoadingIpRights} = useQuery<PagedResponse<PRIpRight>>({
    queryKey: ['pr', 'ip', member.familyMemberId],
    queryFn: () => parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/ips?reference=${member.familyMemberId}`))
  })

  //console.log({data})
  //console.log({cc: data.items[0]?.cost_center_id})

  const ip_partner_patent_ref = '' + member.familyMemberId
  const prIpRight = (data?.items && data.items.find) ? data.items.find(ip => ip.ip_partner_patent_ref === ip_partner_patent_ref) : undefined

  const ip_id = prIpRight?.id
  // TODO: Remove PRInstruction[] after mid May 2026
  const {data: _instructions} = useQuery<PRInstruction[] | PagedResponse<PRInstruction>>({
    queryKey: ['pr', 'instructions', ip_id],
    queryFn: () => parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/ips/${ip_id}/instructions`)),
    enabled: !!ip_id,
  })
  // @ts-ignore  // TODO remove after type change
  const instructions: PRInstruction[] = (_instructions && 'items' in _instructions) ? _instructions.items : _instructions

  const ipRight = prIpRight ? toIpRight(prIpRight, ownerByCostCenterId) : undefined

  // TODO: Remove PRRenewal[] after mid May 2026
  const {data: _prRenewals, isLoading: isLoadingRenewals} = useQuery<PRRenewal[] | PagedResponse<PRRenewal>>({
    queryKey: ['pr', 'renewals', prIpRight?.id],
    queryFn: () => parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/ips/${prIpRight.id}/renewals`)).then(res => res.filter(isEmptyRenewal)),
    enabled: !!prIpRight,
  })
  // @ts-ignore   // TODO remove after type change
  const prRenewals: PRRenewal[] = (_prRenewals && 'items' in _prRenewals) ? _prRenewals.items : _prRenewals

  // console.log({prRenewals})
  const renewals = toRenewals(prIpRight, prRenewals, instructions)

  // console.log({prIpRight, ipRight, renewals, prRenewals, instructionsPerRenewalId})
  const isLoading = isLoadingIpRights || isLoadingRenewals

  return {ipRight, prIpRight, prRenewals, renewals, isLoading}
}

export function useIpRightImport() {
  const queryClient = useQueryClient()

  const refreshCache = () =>
    queryClient.invalidateQueries({
      queryKey: ['pr'],
      refetchType: 'all',
    })


  const { familyById } = usePatents()
  const deriveInitial: (member: Member) => Omit<PrPostIp, 'cost_center_id' | 'annuity_year' | 'number_of_claims'> = (member: Member) => ({
    //internal_patent_ref: '' + member.familyMemberId,
    ip_owner_patent_ref: member.internalReference,
    ip_owner_secondary_patent_ref: member.externalReference ?? null,
    ip_owner_family_ref: familyById[member.patentFamilyId]?.internalReference ?? null,
    ip_partner_patent_ref: '' + member.familyMemberId,

    application_number: member.applicationNumber,
    filing_date: member.applicationDate,
    publication_number: member.publicationNumber,
    publication_date: member.publicationDate,
    // explicitly set to null if not given
    grant_number: member.patentNumber ?? null,
    grant_date: member.patentDate ?? null,

    priority_number: null,
    priority_date: familyById[member.patentFamilyId]?.priorityDate ?? null,

    country: member.countryCode,

    type: toPrIpType(member.ipType),
    region: member.validated ? 'EP' : member.countryCode === 'WO' ? 'WO' : null,

    product_tags: [null, null, null, null, null],
    tags: null,

    pct_filing_date: null,
    pct_filing_number: null,
    pct_publication_date: null,
    pct_publication_number: null,
    local_application_number: null,
    local_publication_number: null,
    local_filing_date: null,
    unitary_date: member.unitaryPatentRegistrationDate ?? null,
    legal_examination_notice_date: null,

    // NOTE if reintroduced, change method return type
    //number_of_claims: member.numberClaims,
    claim_count: member.numberClaims,
  })

  async function _post(ipRight: PrPostIp) {
    const isUpdate = ipRight.id !== undefined
    const url = isUpdate ? `/api/patent-renewal/pass-through/api/v1/ips/${ipRight.id}` : `/api/patent-renewal/pass-through/api/v1/ips`
    const method = isUpdate ? 'PATCH' : 'POST'
    return parseBackend(fetch(url, {
      method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ipRight),
    }))
  }

  async function postIpRight(ipRight: PrPostIp) {
    const result = _post(ipRight)
    refreshCache()
    return result
  }

  async function postIpRights(ipRights: PrPostIp[]) {
    const results = await Promise.all(ipRights.map(ipRight => _post(ipRight)))
    refreshCache()
    return results
  }

  async function stopIpRight(ip_id: number | string) {
    const body = {
      instruction: "cancel",
      reason: "O",
    }
    const result = await parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/ips/${ip_id}/instructions`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }))
    refreshCache()
    return result
  }

  return {
    stopIpRight,
    postIpRight,
    postIpRights,
    deriveInitial,
  }
}

export type PrPostIp = {
  id?: string,
  type: PrIpType,
  grant_number: string | null,
  application_number: string | null,
  publication_number: string | null,
  cost_center_id: string,
  ip_owner_family_ref: string | null,
  ip_owner_patent_ref: string | null,
  ip_partner_patent_ref: string | null,
  priority_number: string | null,
  region?: string | null,
  customer_estimated_next_renewal_date?: string | null,
  country: string,

  product_tags: string[],
  tags: string | null,

  priority_date: string | null,
  pct_filing_date: string | null,
  pct_filing_number: string | null,
  pct_publication_date: string | null,
  pct_publication_number: string | null,
  local_application_number: string | null,
  local_publication_number: string | null,
  local_filing_date: string | null,
  filing_date: string | null,
  publication_date: string | null,
  grant_date: string | null,
  unitary_date: string | null,
  legal_examination_notice_date: string | null,

  ip_owner_secondary_patent_ref: string | null,

  /*
  Scopewise, Sebastian is only addressing number_of_claims,
  as we don’t have a dedicated field in the backend for individual claims.

  For now, please continue using number_of_claims for independent claims as well.
  Specifically, for Vietnam, we would ask you to provide the number of independent claims using this field.

  Properly supporting number_of_individual_claims for both read and write will require database model changes,
  along with some Vietnam-specific backend logic. This would make it a significantly larger task than a simple update.
*/
  number_of_claims: number | null,
  /*
  So far they have chosen to call the fields in the api claims_count, rather then number of claims, for your information.

For the independent claims field on your end, a temporary label could help, but it’s likely not ideal for you. 
For now, you could leave `number_of_claims` empty and provide it when prompted by us?
I don't think this should be a blocker as for example I understand that WoodWelding does not have cases in VN.
Thoughts?

Using the total number of claims would cause a significant overcharge and require extra coordination with our agent, so that's not really an option.

As context, each additional individual claim costs ~€40, typically with 1–10 claims, while total claims can exceed 50, so the cost difference is substantial.
  */
  claim_count: number | null,
}
